import { useCallback, useState } from "react";
import { proofreadEnglish, suggestAgentReplies } from "@/api/writingAssistantApi";

/**
 * AI writing helpers for the chat / ticket composer (suggestions + proofread).
 */
export function useWritingAssistant() {
  const [suggestions, setSuggestions] = useState(/** @type {string[]} */ ([]));
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [proofLoading, setProofLoading] = useState(false);
  const [error, setError] = useState(/** @type {Error | null} */ (null));

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setError(null);
  }, []);

  const fetchSuggestions = useCallback(
    /**
     * @param {{ messages: unknown[], draftText?: string, ticketSubject?: string, ticketSummary?: string }} ctx
     */
    async (ctx) => {
      setSuggestLoading(true);
      setError(null);
      try {
        const { data } = await suggestAgentReplies({
          ...ctx,
          workspaceId: ctx.workspaceId ?? null,
        });
        setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
        setSuggestions([]);
      } finally {
        setSuggestLoading(false);
      }
    },
    []
  );

  const runProofread = useCallback(async (text, workspaceId = null) => {
    setProofLoading(true);
    setError(null);
    try {
      const { data } = await proofreadEnglish({ text, workspaceId });
      return typeof data?.text === "string" ? data.text : text;
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      return text;
    } finally {
      setProofLoading(false);
    }
  }, []);

  return {
    suggestions,
    setSuggestions,
    clearSuggestions,
    fetchSuggestions,
    runProofread,
    suggestLoading,
    proofLoading,
    error,
    clearError: useCallback(() => setError(null), []),
  };
}
